import LeadCaptureBox from "@/components/LeadCaptureBox";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, Zap, Check, ChevronRight, Monitor, Smartphone, Moon, Sun } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const CLIENTS = [
  {
    icon: Monitor,
    label: "Gmail Desktop",
    desc: "The full web client rendering — including how your preheader and subject display in the thread list.",
  },
  {
    icon: Smartphone,
    label: "Gmail Mobile",
    desc: "Android and iOS Gmail app rendering. Mobile clips subject lines at ~35 chars — we show you exactly what gets cut.",
  },
  {
    icon: Monitor,
    label: "Outlook Desktop",
    desc: "Outlook's Word-based rendering engine handles CSS differently. Tables, padding, and web fonts all behave differently here.",
  },
  {
    icon: Moon,
    label: "Dark mode",
    desc: "Across all three clients. Dark mode is now enabled on 82% of mobile devices — seeing how your email inverts is no longer optional.",
  },
];

const WHAT_YOU_CATCH = [
  { issue: "Subject line cut off on mobile", fix: "Shorten to under 35 characters or front-load the key message" },
  { issue: "Images not loading (Outlook)", fix: "Add descriptive alt text so the email still communicates without images" },
  { issue: "White text invisible in dark mode", fix: "Use transparent backgrounds or invert-safe color combinations" },
  { issue: "CTA button misaligned on mobile", fix: "Use full-width buttons with padding rather than inline links on mobile breakpoints" },
  { issue: "Web font falling back to Times New Roman", fix: "Specify reliable fallbacks: Georgia, serif for display; Arial, sans-serif for body" },
  { issue: "Padding collapsing in Outlook", fix: "Use table-based layout with explicit cell padding for Outlook-safe spacing" },
];

export default function InboxPreviewPage() {
  return (
    <>
      <SeoHead
        title="Inbox Preview — MoreMail | See Your Email in Gmail, Outlook & Dark Mode"
        description="Preview your email exactly as it appears in Gmail desktop, Gmail mobile, and Outlook — including dark mode rendering. Catch rendering issues before your subscribers do."
        url={`${SITE_URL}/inbox-preview`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Features", url: `${SITE_URL}/features` },
          { name: "Inbox Preview", url: `${SITE_URL}/inbox-preview` },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-coral/20 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <div className="flex items-center gap-1.5 text-white/40 text-xs font-medium mb-8">
            <Link to="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className="text-white/60">AI Suite</span>
            <ChevronRight size={12} />
            <span className="text-white/80">Inbox Preview</span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral/20 border border-coral/30 rounded-full text-coral text-xs font-bold mb-6">
            <Zap size={11} />
            AI Suite
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5 max-w-3xl">
            What you see is{" "}
            <em className="text-coral not-italic">exactly</em> what they get.
          </h1>

          <p className="text-white/60 text-base sm:text-lg max-w-2xl mb-10 leading-relaxed">
            Preview your email exactly as it renders in Gmail desktop, Gmail mobile, and Outlook — including dark mode. Catch broken layouts, clipped subject lines, and invisible text before you send to thousands.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <LeadCaptureBox />
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-white/70 font-semibold hover:border-white/40 hover:text-white transition-colors"
            >
              All features
            </Link>
          </div>
        </div>
      </section>

      {/* Client cards */}
      <section className="bg-white border-b border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            Four rendering environments, one click
          </h2>
          <p className="text-slate text-sm text-center mb-10">
            Together these four cover over 90% of your subscribers' inboxes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CLIENTS.map((client) => (
              <div key={client.label} className="bg-paper rounded-2xl border border-black/[0.06] p-5 flex gap-4">
                <div className="p-2.5 rounded-xl bg-coral/10 flex-shrink-0 h-fit">
                  <client.icon size={18} className="text-coral" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-ink mb-1.5">{client.label}</h3>
                  <p className="text-xs text-slate leading-relaxed">{client.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dark mode callout */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-4">
              Dark mode isn't optional anymore.
            </h2>
            <p className="text-slate text-sm leading-relaxed mb-4">
              82% of mobile users have dark mode enabled. Emails designed only for light mode can have white text on white backgrounds, logos that disappear, and CTAs that become invisible.
            </p>
            <p className="text-slate text-sm leading-relaxed mb-6">
              MoreMail's dark mode preview renders the exact inversion logic Gmail and Outlook apply — so you can catch these issues while there's still time to fix them.
            </p>
            <div className="flex items-center gap-2">
              <Check size={14} className="text-teal flex-shrink-0" />
              <span className="text-sm text-ink">Side-by-side light and dark mode comparison</span>
            </div>
          </div>

          {/* Visual comparison mockup */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white border border-black/[0.08] p-4 shadow-sm">
              <div className="flex items-center gap-1.5 mb-3">
                <Sun size={11} className="text-amber-400" />
                <span className="text-[10px] font-bold text-slate uppercase tracking-wider">Light</span>
              </div>
              <div className="space-y-2">
                <div className="h-2.5 bg-ink/80 rounded w-3/4" />
                <div className="h-2 bg-ink/20 rounded w-full" />
                <div className="h-2 bg-ink/20 rounded w-5/6" />
                <div className="h-2 bg-ink/20 rounded w-4/5" />
                <div className="h-7 bg-coral rounded-full mt-3" />
              </div>
            </div>
            <div className="rounded-xl bg-[#1c1c1e] border border-white/10 p-4 shadow-sm">
              <div className="flex items-center gap-1.5 mb-3">
                <Moon size={11} className="text-blue-400" />
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Dark</span>
              </div>
              <div className="space-y-2">
                <div className="h-2.5 bg-white/80 rounded w-3/4" />
                <div className="h-2 bg-white/20 rounded w-full" />
                <div className="h-2 bg-white/20 rounded w-5/6" />
                <div className="h-2 bg-white/20 rounded w-4/5" />
                <div className="h-7 bg-coral rounded-full mt-3" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common issues */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
            What you'll catch before they do
          </h2>
          <p className="text-slate text-sm text-center mb-10">The most common rendering issues found in pre-send previews.</p>
          <div className="rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="grid grid-cols-2 bg-paper border-b border-black/[0.06]">
              <div className="px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate">Issue caught in preview</div>
              <div className="px-5 py-3 text-xs font-bold uppercase tracking-widest text-teal border-l border-black/[0.06]">How to fix it</div>
            </div>
            {WHAT_YOU_CATCH.map((row, i) => (
              <div key={i} className={`grid grid-cols-2 ${i % 2 === 0 ? "bg-white" : "bg-paper/50"}`}>
                <div className="px-5 py-4 text-sm text-slate border-b border-black/[0.04] flex items-start gap-2">
                  <span className="text-red-300 mt-0.5 flex-shrink-0">!</span>
                  {row.issue}
                </div>
                <div className="px-5 py-4 text-sm text-ink border-b border-black/[0.04] border-l border-black/[0.06] flex items-start gap-2">
                  <Check size={13} className="text-teal mt-0.5 flex-shrink-0" />
                  {row.fix}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile subject line callout */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <div className="bg-paper rounded-2xl border border-black/[0.06] p-6 sm:p-8">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mb-3">
            The subject line preview that saves campaigns
          </h2>
          <p className="text-slate text-sm leading-relaxed mb-6 max-w-2xl">
            Gmail mobile truncates subject lines at ~35 characters in portrait mode. Most marketers write for desktop (50+ chars) and never see what mobile subscribers see.
            MoreMail's inbox preview shows both side by side — with a visual indicator of exactly where the cutoff lands on each device.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-black/[0.06] p-4">
              <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-2">Gmail Desktop</p>
              <p className="text-sm text-ink font-medium">Your Q4 product update + 3 things to try this weekend</p>
              <p className="text-xs text-teal mt-1 flex items-center gap-1"><Check size={10} /> Full subject visible</p>
            </div>
            <div className="bg-white rounded-xl border border-black/[0.06] p-4">
              <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-2">Gmail Mobile</p>
              <p className="text-sm text-ink font-medium">Your Q4 product update +<span className="text-red-300">...</span></p>
              <p className="text-xs text-red-400 mt-1">Truncated after 28 chars</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="bg-white border-t border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-xl font-bold text-ink mb-6">Works great with</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Spam Score", href: "/spam-score", desc: "Check spam risk alongside rendering — full pre-send confidence in one place." },
              { label: "AI Campaign Builder", href: "/ai-campaign-builder", desc: "The AI generates HTML that's already tested for cross-client compatibility." },
              { label: "Subject Line Optimisation", href: "/subject-line-optimisation", desc: "Test variants knowing exactly how each will display on mobile." },
            ].map((f) => (
              <Link
                key={f.label}
                to={f.href}
                className="bg-paper rounded-2xl border border-black/[0.06] p-5 hover:border-coral/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-bold text-sm text-ink mb-1.5 group-hover:text-coral transition-colors flex items-center justify-between">
                  {f.label} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-xs text-slate leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            No more "looked fine in preview."
          </h2>
          <p className="text-white/80 text-base mb-8">
            See exactly what your subscribers see — before you send. Free on every plan.
          </p>
          <div className="flex justify-center"><LeadCaptureBox /></div>
        </div>
      </section>
    </>
  );
}
